import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mic2, ArrowLeft, Loader2 } from "lucide-react";
import { createRoom } from "@/lib/api";
import { setSession } from "@/lib/session";

export default function HostCreate() {
  const navigate = useNavigate();
  const [roomName, setRoomName] = useState("");
  const [hostNick, setHostNick] = useState("");
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!roomName.trim()) return;
    setLoading(true);
    try {
      const data = await createRoom({
        room_name: roomName.trim(),
        host_nickname: hostNick.trim() || "Host",
        password: usePassword && password ? password : null,
      });
      // Persist host token locally
      localStorage.setItem(
        `host_${data.room_code}`,
        JSON.stringify({
          host_token: data.host_token,
          role: "host",
          room_name: data.room_name,
          host_nickname: hostNick.trim() || "Host",
          created_at: data.created_at,
        })
      );
      setSession({
        role: "host",
        room_code: data.room_code,
        room_name: data.room_name,
        host_nickname: hostNick.trim() || "Host",
      })
      toast.success(`Room ${data.room_code} created`);
      navigate(`/host/${data.room_code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create room");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ambient-bg min-h-screen relative">
      <div className="grain" />
      <div className="relative z-10 max-w-md mx-auto px-6 py-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-12"
          data-testid="back-to-landing"
        >
          <ArrowLeft size={18} /> Back
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-4xl font-bold text-white font-[Outfit] mb-2">
            Create a Room
          </h1>
          <p className="text-zinc-400 mb-10">
            Open this on the device connected to your TV.
          </p>

          <form onSubmit={handleCreate} className="space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                Room Name
              </label>
              <input
                data-testid="room-name-input"
                type="text"
                className="input-base"
                placeholder="Saturday Night Karaoke"
                value={roomName}
                onChange={(e) => setRoomName(e.target.value)}
                autoFocus
                maxLength={80}
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                Your Name (Host)
              </label>
              <input
                data-testid="host-nickname-input"
                type="text"
                className="input-base"
                placeholder="Host"
                value={hostNick}
                onChange={(e) => setHostNick(e.target.value)}
                maxLength={40}
              />
            </div>

            <label className="flex items-center gap-3 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 cursor-pointer">
              <input
                data-testid="use-password-checkbox"
                type="checkbox"
                checked={usePassword}
                onChange={(e) => setUsePassword(e.target.checked)}
                className="w-4 h-4 accent-purple-500"
              />
              <span className="text-zinc-200 text-sm flex-1">
                Password-protect this room
              </span>
            </label>

            {usePassword && (
              <input
                data-testid="room-password-input"
                type="text"
                className="input-base"
                placeholder="Set a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}

            <button
              data-testid="create-room-submit"
              type="submit"
              disabled={loading || !roomName.trim()}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <Mic2 size={18} />
              )}
              {loading ? "Creating..." : "Create Room"}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
